import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type OfflineDetectionOptions = {
  /**
   * URL hit to verify connectivity. Should resolve quickly and be cacheable.
   */
  pingUrl: string;
  /**
   * How long to wait before aborting a ping request.
   */
  timeoutMs: number;
  /**
   * How often to poll for connectivity while offline (in ms).
   */
  pollIntervalMs: number;
  /**
   * Number of consecutive failed pings required before we consider the app offline.
   */
  failureThreshold: number;
  /**
   * Delay after the browser reports we are back online before confirming via ping.
   */
  recoveryDebounceMs: number;
};

const DEFAULT_OPTIONS: OfflineDetectionOptions = {
  pingUrl: '/ping.txt',
  timeoutMs: 4000,
  pollIntervalMs: 15000,
  failureThreshold: 2,
  recoveryDebounceMs: 3000,
};

// `navigator.onLine === false` is only a hint. Chrome reports it for users on
// VPNs / virtual adapters who are fully connected, so it must never decide the
// offline state on its own: the ping is the source of truth. The flag only
// lowers the failure threshold so a genuinely unplugged user is told quickly.
const isNavigatorOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine;

export const useOfflineDetection = (
  customOptions?: Partial<OfflineDetectionOptions>
) => {
  const options = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...customOptions,
    }),
    [customOptions]
  );

  const [isOffline, setIsOffline] = useState(false);
  const failuresRef = useRef(0);
  const mountedRef = useRef(true);
  const pingInFlightRef = useRef(false);
  const recoveryTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const markOffline = useCallback(() => {
    failuresRef.current = options.failureThreshold;
    if (mountedRef.current) {
      setIsOffline(true);
    }
  }, [options.failureThreshold]);

  const markOnline = useCallback(() => {
    failuresRef.current = 0;
    if (mountedRef.current) {
      setIsOffline(false);
    }
  }, []);

  const ping = useCallback(async () => {
    // One ping at a time: an older request resolving after a newer one would
    // otherwise overwrite the fresher result.
    if (pingInFlightRef.current) {
      return false;
    }
    pingInFlightRef.current = true;

    const controller = new AbortController();

    const timeoutId = window.setTimeout(
      () => controller.abort(),
      options.timeoutMs
    );

    try {
      const cacheBuster = `ping=${Date.now()}`;
      const separator = options.pingUrl.includes('?') ? '&' : '?';

      const response = await fetch(
        `${options.pingUrl}${separator}${cacheBuster}`,
        {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`Ping request failed with status ${response.status}`);
      }

      window.clearTimeout(timeoutId);
      markOnline();
      return true;
    } catch (error) {
      window.clearTimeout(timeoutId);
      failuresRef.current += 1;
      const threshold = isNavigatorOnline() ? options.failureThreshold : 1;
      if (failuresRef.current >= threshold) {
        markOffline();
      }
      return false;
    } finally {
      pingInFlightRef.current = false;
    }
  }, [
    markOffline,
    markOnline,
    options.failureThreshold,
    options.pingUrl,
    options.timeoutMs,
  ]);

  useEffect(() => {
    // Ensure we run a connectivity check on mount.
    ping();

    const intervalId = window.setInterval(() => {
      void ping();
    }, options.pollIntervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [options.pollIntervalMs, ping]);

  useEffect(() => {
    const handleOnline = () => {
      if (recoveryTimeoutRef.current !== null) {
        window.clearTimeout(recoveryTimeoutRef.current);
      }

      recoveryTimeoutRef.current = window.setTimeout(() => {
        recoveryTimeoutRef.current = null;
        void ping();
      }, options.recoveryDebounceMs);
    };

    const handleOffline = () => {
      // Re-check immediately rather than trusting the event: the ping fails
      // fast when we are really offline and the lowered threshold flips us.
      void ping();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (recoveryTimeoutRef.current !== null) {
        window.clearTimeout(recoveryTimeoutRef.current);
        recoveryTimeoutRef.current = null;
      }
    };
  }, [options.recoveryDebounceMs, ping]);

  return isOffline;
};
